import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Checkbox,
  Popconfirm,
  Tabs,
  Button,
} from "antd";
import { GroupeService } from "../../services/GroupeService";
import { catchError } from "../../services/DaoService";
import Toast from "../../helpers/Toast";
import styles from "./GroupeDetails.module.scss";
import { useNavigate, useParams } from "react-router-dom";
import Main from "../../components/layouts/Main";
import UserGroupeList from "../../components/user/UserGroupeList";
import LoadingPlaceholder from "../../components/utils/LoadingPlaceholder";
import { EditFilled, DeleteFilled, UsergroupAddOutlined, ReloadOutlined } from "@ant-design/icons";
import GroupeEdit from "../../components/groupe/GroupeEdit";
import PageTitle from "../../components/utils/PageTitle";
import LogTraces from "../../components/logtrace/LogTraces";
import { useUserContext } from "../../contexts/UserContext";
import Breadcrumb from "../../components/utils/Breadcrumb";


const { Paragraph, Text, Title } = Typography;

const GroupeDetailsPage = () => {
  const { uid } = useParams();
  const [loading, setLoading] = useState(true);
  const [groupeData, setGroupeData] = useState(null);
  const [permissionsData, setPermissionsData] = useState([]);
  const { check } = useUserContext();
  const [editModalVisible, setEditModalVisible] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [groupes, setGroupes] = React.useState([]);
  const navigate = useNavigate();

  const fetchData = useCallback(() => {
    setLoading(true);
    Promise.all([
      GroupeService.getInstance().getPermissions(),
      GroupeService.getInstance().find(uid),
    ])
      .then(([permissionsResponse, groupeResponse]) => {
        setPermissionsData(permissionsResponse.data);
        setGroupeData(groupeResponse.data);
      })
      .catch((error) => {
        catchError(error);
        Toast.error("Erreur lors du chargement des données.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [uid]);


  useEffect(() => {
    setLoading(true);
    Promise.all([
      GroupeService.getInstance().getPermissions(),
      GroupeService.getInstance().find(uid),
    ])
      .then(([permissionsResponse, groupeResponse]) => {
        setPermissionsData(permissionsResponse.data);
        setGroupeData(groupeResponse.data);
      })
      .catch((error) => {
        catchError(error);
        Toast.error("Erreur lors du chargement des données.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [uid, editModalVisible]);

  const tabItems = [
    {
      key: "permissions",
      label: "Permissions",
      children: (
        <Row>
          <Col span={24}>
            <Title level={4}>Permissions</Title>
            {permissionsData.map((group) => (
              <div key={group.code} className={styles.permissionsGroup}>
                <Title level={5}>{group.nom}</Title>
                <Row>
                  {group.permissions.map((permission) => (
                    <Col span={12} key={permission.code}>
                      <Checkbox
                        checked={groupeData.permissions.includes(
                          permission.code
                        )}
                        disabled
                      >
                        {permission.nom}
                      </Checkbox>
                    </Col>
                  ))}
                </Row>
              </div>
            ))}
          </Col>
        </Row>
      ),
    },
    {
      key: "users",
      label: "Utilisateurs",
      children: <UserGroupeList groupeUid={uid} />,
    },
    check('MANAGE-DATA-OPERATIONS-HISTORY') && groupeData && {
      key: "trace-logs",
      label: "Logs",
      children: <LogTraces target={'groupe'} parentId={groupeData?.id} />
    },
  ];

  const setItemToEdit = (groupe) => {
    setGroupeData(groupe);
    setEditModalVisible(true);
  };
  const handleGroupeUpdate = () => {
    setEditModalVisible(false);
  };
  
  const removeItem = (groupe) => {
    setIsDeleting(true);
    GroupeService.getInstance()
      .remove(groupe.uid)
      .then(() => {
        setGroupes(groupes.filter(g => g.id !== groupe.id));
        navigate('/groupe');
      }).catch(error => {
        catchError(error);
      }).finally(() => {
        setIsDeleting(false);
      });

  }

  return (
    <Main>
      <Breadcrumb parentLink="/groupe" canSee={check('VIEW-GROUPS')} parentLinkTitle="Liste des groupes" title={groupeData?.nom ?? 'En chargement...'} />
      <PageTitle title={groupeData?.nom ?? 'En chargement...'} icon={<UsergroupAddOutlined />}
        backPageTitle={"Liste des groupes"} backPageLink={"/groupe"}
        canSeeBackPageLink={check("VIEW-GROUPS")} subTitle={groupeData?.description}>
        <Button
          onClick={fetchData}
          className={styles.refreshButton}
        >
          <ReloadOutlined spin={loading} className={styles.refreshIcon} />
          Rafraîchir
        </Button>
        {check("EDIT-GROUP") ? (
          <Button
            onClick={() => setItemToEdit(groupeData)}
            icon={<EditFilled />}
            className="btn btn-warning"
          >Modifier</Button>
        ) : null}
        {check("DELETE-GROUP") ? (
          <Popconfirm
            title="Voulez-vous vraiment supprimer ce groupe?"
            onConfirm={() => {
              removeItem(groupeData);
            }}
            okText="Supprimer"
            cancelText="Annuler"
            okType="danger"
          >
            <Button
              loading={isDeleting}
              type="primary"
              danger
              icon={<DeleteFilled />}
            >Supprimer</Button>
          </Popconfirm>
        ) : null}
      </PageTitle>
      {loading ? (
        <LoadingPlaceholder />
      ) : (
        <>

          <Card className="mb-2">
            <GroupeEdit
              visible={editModalVisible}
              uid={uid}
              onUpdate={handleGroupeUpdate}
              onCancel={() => setEditModalVisible(false)}
            />
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Paragraph>
                  <Text strong>Nom :</Text> {groupeData.nom}
                </Paragraph>
              </Col>
              <Col xs={24} md={12}>
                <Paragraph>
                  <Text strong>Code :</Text> {groupeData.code}
                </Paragraph>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <Paragraph>
                  <Text strong>Description :</Text> {groupeData.description}
                </Paragraph>
              </Col>
            </Row>
          </Card>
          <Card bordered>
            <Tabs defaultActiveKey="permissions" items={tabItems} />
          </Card>
        </>
      )}
    </Main>
  );
};

export default GroupeDetailsPage;
